import React, { FC, useCallback, useState, useRef, Fragment } from 'react';
import Svg from 'svg-inline-react';
import classnames from 'classnames';
import { debounce } from 'lodash';
import GlobeSvg from '@shared/assets/svgs/globe.svg';
import CloseSvg from '@src/svgs/close.svg';
import { TextField } from '@src/components/TextField';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { GetPlacesQuery, PlaceTypeEnum, useGetPlacesLazyQuery } from '@src/graphql/generated';
import { OutsideClick } from '@src/components/OutsideClick';
import { useAppSelector } from '@src/redux';
import { NoticeState } from '@src/redux/reducers/notice/notice.interface';
import { getNotice } from '@src/redux/selectors/notice';
import { PlacesSearchDropdown } from '@src/containers/Source/PlacesSearchDropdown';
import { PlacesSearchProps } from './props';
import './styles.scss';

export const PlacesSearch: FC<PlacesSearchProps> = ({
  onPlaceSelect,
  isRelocation,
  isRemote,
  onRelocationToggle,
  onRemoteToggle,
  isOnSite,
  onSiteToggle,
  onSelectGlobal,
  selectedPlace,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [getPlaces, { data, loading: isLocationsLoading }] = useGetPlacesLazyQuery();

  const [searchValue, setSearchValue] = useState(selectedPlace?.name || '');

  const inputRef = useRef<HTMLInputElement>(null);

  const noticeData = useAppSelector<NoticeState>(getNotice);

  const locations = data?.places.result || [];

  const handleToggle = (isOpen: boolean) => {
    setIsOpen(isOpen);
  };

  const setPlace = (place: GetPlacesQuery['places']['result'][0]) => {
    if (place?.entity?.id) {
      onPlaceSelect({
        id: place.entity.id,
        name: place.entity.longName,
        type: place.type,
      });

      setSearchValue(place.entity?.longName || '');
      handleToggle(false);

      if (inputRef.current) {
        inputRef.current.value = place.entity?.longName;
      }

      trackEvent({
        context: MetricContext.SOURCING_ENTRY_PAGE,
        interaction: MetricAction.SEARCH_PLACES,
        payload: {
          'Filter Type': 'Country or City',
          'Filter Value': place.entity?.longName,
        },
      });
    }
  };

  const handleGetPlaces = useCallback(
    debounce((searchValue: string) => {
      getPlaces({
        variables: {
          paginationOptions: {
            limit: 8,
          },
          placeTypes: [PlaceTypeEnum.LOCATION, PlaceTypeEnum.COUNTRY],
          searchPattern: searchValue.trim(),
        },
      });
    }, 350),
    [],
  );

  const onSearch = (searchValue: string) => {
    const doesValueExist = !!searchValue?.trim().length;

    handleGetPlaces(searchValue);
    setSearchValue(searchValue);
    handleToggle(doesValueExist);

    if (!doesValueExist) {
      onClear();
    }
  };

  const doesNoticeExist = !noticeData.isLoading && !!noticeData.notice;

  return (
    <Fragment>
      <div
        className={classnames(
          'start-headhunt-location-search__backdrop',
          isOpen && 'start-headhunt-location-search__backdrop--shown',
          doesNoticeExist && 'start-headhunt-location-search__backdrop--small',
        )}
      />

      <OutsideClick addOutsideClickListener={isOpen} onOutsideClick={() => handleToggle(false)}>
        <div className="start-headhunt-location-search">
          <Svg
            className={classnames(
              'start-headhunt-location-search__icon',
              isOpen && 'start-headhunt-location-search__icon--active',
            )}
            src={GlobeSvg}
          />

          <TextField
            containerClassName="start-headhunt-location-search__container"
            defaultValue={searchValue}
            inputClassName="start-headhunt-location-search__field"
            onChange={(searchValue: string) => onSearch(searchValue)}
            onFocus={() => handleToggle(true)}
            placeholder="Location"
            wrapperClassName="start-headhunt-location-search__wrapper"
          />

          {searchValue && (
            <Svg
              className="start-headhunt-location-search__clear"
              src={CloseSvg}
              onClick={() => {
                setSearchValue('');
                onClear();
              }}
            />
          )}

          {isOpen && (
            <PlacesSearchDropdown
              isLoading={isLocationsLoading}
              isOnSite={isOnSite}
              isRelocation={isRelocation}
              isRemote={isRemote}
              locations={locations}
              onRelocationToggle={onRelocationToggle}
              onRemoteToggle={onRemoteToggle}
              onSelectGlobal={onSelectGlobal}
              onSiteToggle={onSiteToggle}
              searchValue={searchValue}
              setPlace={setPlace}
            />
          )}
        </div>
      </OutsideClick>
    </Fragment>
  );
};
